// Template renderers — one per `kind` in backend/lib/template-schema.js.
//
// Why this lives in its own file: the same code is needed in manager-web
// for live mini-previews in the template picker. We don't have a shared JS
// package set up yet (design-system is CSS + i18n only), so the manager-web
// copy at manager-web/src/components/TemplatePreview.jsx must stay in sync
// with this one. Both files are intentionally framework-light (no router,
// no auth, no global state) so copying is cheap.
//
// All renderers fill 100% of their container — the container decides the
// physical size (player.jsx uses 100vw/100vh, the manager picker uses a
// 16:9 card). All font sizes use clamp() against the container so the
// design holds up at TV sizes (1080p–4K) AND at 240×135 thumbnails.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

// ─── Animations ───────────────────────────────────────────────────────────
// Per-element entrance/loop animations. Keyframes are injected once on first
// render via <AnimationStyles />. Each helper is pure so it can be used in
// both the player (this file) and the editor (which gets these same helpers
// duplicated in manager-web/src/components/TemplatePreview.jsx).
//
// Shape of `animation`:
//   { type, direction, duration, delay, easing, loop }
// Defaults are filled in by the schema validator so we don't have to here.
const ANIM_KEYFRAMES = `
@keyframes opsora-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes opsora-slide-up    { from { opacity: 0; transform: translateY( 30px); } to { opacity: 1; transform: translateY(0); } }
@keyframes opsora-slide-down  { from { opacity: 0; transform: translateY(-30px); } to { opacity: 1; transform: translateY(0); } }
@keyframes opsora-slide-left  { from { opacity: 0; transform: translateX( 60px); } to { opacity: 1; transform: translateX(0); } }
@keyframes opsora-slide-right { from { opacity: 0; transform: translateX(-60px); } to { opacity: 1; transform: translateX(0); } }
@keyframes opsora-zoom-in  { from { opacity: 0; transform: scale(0.6); } to { opacity: 1; transform: scale(1); } }
@keyframes opsora-zoom-out { from { opacity: 0; transform: scale(1.4); } to { opacity: 1; transform: scale(1); } }
@keyframes opsora-bounce {
  0%   { opacity: 0; transform: translateY(-40px); }
  60%  { opacity: 1; transform: translateY( 12px); }
  80%  { transform: translateY(-6px); }
  100% { transform: translateY(0); }
}
@keyframes opsora-rotate { from { opacity: 0; transform: rotate(-180deg); } to { opacity: 1; transform: rotate(0); } }
@keyframes opsora-pulse {
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.06); }
}
@keyframes opsora-flip-y { from { opacity: 0; transform: perspective(800px) rotateY(-180deg); } to { opacity: 1; transform: perspective(800px) rotateY(0); } }
@keyframes opsora-flip-x { from { opacity: 0; transform: perspective(800px) rotateX(-180deg); } to { opacity: 1; transform: perspective(800px) rotateX(0); } }
@keyframes opsora-flash {
  0%, 50%, 100% { opacity: 1; }
  25%, 75%      { opacity: 0.25; }
}
@keyframes opsora-shake {
  0%, 100%                   { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90%    { transform: translateX(-6px); }
  20%, 40%, 60%, 80%         { transform: translateX( 6px); }
}
@keyframes opsora-glow {
  0%, 100% { filter: drop-shadow(0 0 4px rgba(255,255,255,0.25)); }
  50%      { filter: drop-shadow(0 0 20px rgba(255,255,255,0.85)); }
}
/* Continuous scroll (4 directions). Same clip+track structure as marquee. */
@keyframes opsora-scroll-left  { 0% { transform: translateX( 100%); } 100% { transform: translateX(-100%); } }
@keyframes opsora-scroll-right { 0% { transform: translateX(-100%); } 100% { transform: translateX( 100%); } }
@keyframes opsora-scroll-up    { 0% { transform: translateY( 100%); } 100% { transform: translateY(-100%); } }
@keyframes opsora-scroll-down  { 0% { transform: translateY(-100%); } 100% { transform: translateY( 100%); } }
@keyframes opsora-marquee      { 0% { transform: translateX( 100%); } 100% { transform: translateX(-100%); } }

/* The base class lives on the element wrapper. Per-type subclasses set the
   keyframe name; CSS variables on the inline style provide duration / delay /
   easing / iteration count. */
.opsora-anim                { animation-name: none; animation-fill-mode: both;
                              animation-duration: var(--animation-duration, 800ms);
                              animation-delay:    var(--animation-delay,    0ms);
                              animation-timing-function: var(--animation-easing, ease-out);
                              animation-iteration-count: var(--animation-iter, 1); }
.opsora-anim-fade           { animation-name: opsora-fade; }
.opsora-anim-slide-up       { animation-name: opsora-slide-up; }
.opsora-anim-slide-down     { animation-name: opsora-slide-down; }
.opsora-anim-slide-left     { animation-name: opsora-slide-left; }
.opsora-anim-slide-right    { animation-name: opsora-slide-right; }
.opsora-anim-zoom-in        { animation-name: opsora-zoom-in; }
.opsora-anim-zoom-out       { animation-name: opsora-zoom-out; }
.opsora-anim-bounce         { animation-name: opsora-bounce; }
.opsora-anim-rotate         { animation-name: opsora-rotate; }
.opsora-anim-pulse          { animation-name: opsora-pulse;  animation-direction: alternate; }
.opsora-anim-flip-y         { animation-name: opsora-flip-y; }
.opsora-anim-flip-x         { animation-name: opsora-flip-x; }
.opsora-anim-flash          { animation-name: opsora-flash; }
.opsora-anim-shake          { animation-name: opsora-shake; }
.opsora-anim-glow           { animation-name: opsora-glow; }

/* ── CapCut Phase 1 — Page transition keyframes. Two pages overlap for
   the transition duration; the outgoing page plays the *-leave keyframe,
   the incoming page plays the *-enter keyframe. */
@keyframes opsora-tx-fade-enter        { from { opacity: 0; }                       to   { opacity: 1; } }
@keyframes opsora-tx-fade-leave        { from { opacity: 1; }                       to   { opacity: 0; } }
@keyframes opsora-tx-dissolve-enter    { from { opacity: 0; filter: blur(8px); }    to   { opacity: 1; filter: blur(0); } }
@keyframes opsora-tx-dissolve-leave    { from { opacity: 1; }                       to   { opacity: 0; } }
@keyframes opsora-tx-slide-left-enter  { from { transform: translateX( 100%); }     to   { transform: translateX(0); } }
@keyframes opsora-tx-slide-left-leave  { from { transform: translateX(0); }         to   { transform: translateX(-100%); } }
@keyframes opsora-tx-slide-right-enter { from { transform: translateX(-100%); }     to   { transform: translateX(0); } }
@keyframes opsora-tx-slide-right-leave { from { transform: translateX(0); }         to   { transform: translateX( 100%); } }
@keyframes opsora-tx-slide-up-enter    { from { transform: translateY( 100%); }     to   { transform: translateY(0); } }
@keyframes opsora-tx-slide-up-leave    { from { transform: translateY(0); }         to   { transform: translateY(-100%); } }
@keyframes opsora-tx-slide-down-enter  { from { transform: translateY(-100%); }     to   { transform: translateY(0); } }
@keyframes opsora-tx-slide-down-leave  { from { transform: translateY(0); }         to   { transform: translateY( 100%); } }
@keyframes opsora-tx-zoom-in-enter     { from { opacity: 0; transform: scale(0.9); } to  { opacity: 1; transform: scale(1); } }
@keyframes opsora-tx-zoom-in-leave     { from { opacity: 1; }                       to   { opacity: 0; } }
@keyframes opsora-tx-zoom-out-enter    { from { opacity: 0; }                       to   { opacity: 1; } }
@keyframes opsora-tx-zoom-out-leave    { from { opacity: 1; transform: scale(1); }  to   { opacity: 0; transform: scale(0.9); } }
@keyframes opsora-tx-wipe-left-enter   { from { clip-path: inset(0 0 0 100%); }     to   { clip-path: inset(0 0 0 0); } }
@keyframes opsora-tx-wipe-left-leave   { from { clip-path: inset(0 0 0 0); }        to   { clip-path: inset(0 100% 0 0); } }
@keyframes opsora-tx-wipe-right-enter  { from { clip-path: inset(0 100% 0 0); }     to   { clip-path: inset(0 0 0 0); } }
@keyframes opsora-tx-wipe-right-leave  { from { clip-path: inset(0 0 0 0); }        to   { clip-path: inset(0 0 0 100%); } }

.opsora-tx-layer {
  position: absolute; inset: 0;
  animation-fill-mode: both;
  animation-duration: var(--tx-duration, 600ms);
  animation-timing-function: ease-in-out;
}

/* Marquee + scroll share the clip+track layout. The outer wrapper is the
   overflow clip; the inner track translates to scroll the content. */
.opsora-marquee-clip        { overflow: hidden; white-space: nowrap; }
.opsora-marquee-track       { display: inline-block; will-change: transform;
                              animation: opsora-marquee var(--animation-duration, 8000ms) linear var(--animation-delay, 0ms) infinite; }
.opsora-scroll-clip         { overflow: hidden; }
.opsora-scroll-track-left   { display: inline-block; will-change: transform;
                              animation: opsora-scroll-left  var(--animation-duration, 8000ms) linear var(--animation-delay, 0ms) infinite; }
.opsora-scroll-track-right  { display: inline-block; will-change: transform;
                              animation: opsora-scroll-right var(--animation-duration, 8000ms) linear var(--animation-delay, 0ms) infinite; }
.opsora-scroll-track-up     { display: block; will-change: transform;
                              animation: opsora-scroll-up    var(--animation-duration, 8000ms) linear var(--animation-delay, 0ms) infinite; }
.opsora-scroll-track-down   { display: block; will-change: transform;
                              animation: opsora-scroll-down  var(--animation-duration, 8000ms) linear var(--animation-delay, 0ms) infinite; }
`;

// Returns the CSS class name(s) to apply to the element wrapper for the
// chosen animation. Marquee and scroll have a separate render path (see
// CanvasElement) — they return '' here because the keyframes go on an
// inner track div, not the outer wrapper.
function getAnimationClass(animation) {
  const a = animation || {};
  if (!a.type || a.type === 'none') return '';
  if (a.type === 'marquee' || a.type === 'scroll') return '';
  const dir = a.direction || 'up';
  let suffix = a.type;
  if (a.type === 'slide')   suffix = `slide-${dir}`;
  if (a.type === 'zoom')    suffix = 'zoom-in';   // back-compat: v8/v9 'zoom' → zoom-in
  if (a.type === 'flip')    suffix = dir === 'left' || dir === 'right' ? 'flip-y' : 'flip-x';
  return `opsora-anim opsora-anim-${suffix}`;
}

// Returns inline style props (CSS custom properties) for the animation. Goes
// on the same wrapper as the class. We never overwrite existing transforms;
// rotation already sets `transform` so we let the keyframes handle motion
// via opacity / scale where possible.
function getAnimationStyle(animation) {
  const a = animation || {};
  if (!a.type || a.type === 'none') return null;
  // Pulse / marquee / scroll / flash / shake / glow all loop by default;
  // others honour the user's `loop` toggle.
  const alwaysLoop = ['pulse', 'marquee', 'scroll', 'flash', 'shake', 'glow'];
  const iter = a.loop || alwaysLoop.includes(a.type) ? 'infinite' : '1';
  return {
    '--animation-duration': `${a.duration ?? 800}ms`,
    '--animation-delay':    `${a.delay ?? 0}ms`,
    '--animation-easing':   a.easing || 'ease-out',
    '--animation-iter':     iter,
  };
}

// One-shot stylesheet injection. Idempotent — re-mounting does nothing.
function AnimationStyles() {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (document.getElementById('opsora-anim-styles')) return;
    const tag = document.createElement('style');
    tag.id = 'opsora-anim-styles';
    tag.textContent = ANIM_KEYFRAMES;
    document.head.appendChild(tag);
  }, []);
  return null;
}

export { getAnimationClass, getAnimationStyle, AnimationStyles, ANIM_KEYFRAMES };

// ─── CapCut Phase 2 — keyframe interpolation ─────────────────────────────
// `keyframes` is a sorted (by t) array of { t, x?, y?, w?, h?, rotation?,
// opacity? } where t is seconds-from-page-start. interpolateKeyframes
// returns a partial override object for the element AT a given time, with
// LINEAR interpolation between bracketing keyframes (each property tracked
// independently so different props can be keyframed on different schedules).
function interpolateKeyframes(keyframes, elapsedSec) {
  if (!Array.isArray(keyframes) || keyframes.length === 0) return null;
  // Pro Phase D — 'speed' supports video speed ramping. VideoEl reads
  // el.speed in its useEffect, so the interpolated value flows through
  // naturally once written onto the effective element.
  const props = ['x', 'y', 'w', 'h', 'rotation', 'opacity', 'speed'];
  const out = {};
  for (const p of props) {
    // Gather just the keyframes that define this property.
    const seq = keyframes.filter((k) => k[p] != null);
    if (seq.length === 0) continue;
    if (seq.length === 1 || elapsedSec <= seq[0].t) { out[p] = seq[0][p]; continue; }
    const last = seq[seq.length - 1];
    if (elapsedSec >= last.t) { out[p] = last[p]; continue; }
    // Find bracketing pair.
    for (let i = 0; i < seq.length - 1; i++) {
      const a = seq[i], b = seq[i + 1];
      if (elapsedSec >= a.t && elapsedSec <= b.t) {
        const span = (b.t - a.t) || 1e-6;
        const tNorm = (elapsedSec - a.t) / span;
        out[p] = a[p] + (b[p] - a[p]) * tNorm;
        break;
      }
    }
  }
  return Object.keys(out).length ? out : null;
}

// Wraps a CanvasElement and re-renders at frame rate to apply interpolated
// overrides. Skips the rAF loop entirely when the element has no keyframes
// (so the cost is paid only by animated elements). When `forcedTime` is
// provided (editor scrubbing), we use that fixed time instead of computing
// elapsed from a pageStartedAt clock.
function KeyframedElement({ el, scale, pageStartedAt, forcedTime }) {
  const hasKeyframes = Array.isArray(el.keyframes) && el.keyframes.length > 0;
  // CapCut Phase 5 — visibility window means the element may need to
  // appear/disappear during page playback. Tick once per frame whenever
  // EITHER keyframes are present OR a visibility window is set, so the
  // visibility state stays current. Skip the loop entirely otherwise.
  const hasWindow = (el.visibleFrom && el.visibleFrom > 0)
                 || (el.visibleTo != null);
  const needsTick = hasKeyframes || hasWindow;
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!needsTick || forcedTime != null) return;
    let rafId;
    const tickFn = () => { setTick((n) => (n + 1) % 1e6); rafId = requestAnimationFrame(tickFn); };
    rafId = requestAnimationFrame(tickFn);
    return () => cancelAnimationFrame(rafId);
  }, [needsTick, forcedTime]);
  // eslint-disable-next-line no-unused-vars
  const _ = tick;

  const elapsed = forcedTime != null
    ? forcedTime
    : Math.max(0, (performance.now() - (pageStartedAt || 0)) / 1000);

  // Visibility gate. If outside the window the element doesn't render —
  // analogous to el.hidden but driven by time. Editor surfaces a "ghost"
  // dashed outline for elements outside their window so the user can
  // still click/select them; that's handled in the editor selection
  // overlay, not here.
  const vFrom = el.visibleFrom || 0;
  const vTo   = el.visibleTo;
  if (elapsed < vFrom - 1e-3) return null;
  if (vTo != null && elapsed > vTo + 1e-3) return null;

  let effectiveEl = el;
  if (hasKeyframes) {
    const ov = interpolateKeyframes(el.keyframes, elapsed);
    if (ov) effectiveEl = { ...el, ...ov };
  }
  return <CanvasElement el={effectiveEl} scale={scale} />;
}

// ─── kind dispatch ────────────────────────────────────────────────────────
export default function TemplateRenderer({ template, scale = 1, animationKey, activePageIdx, playheadSec }) {
  // Phase 10.7 — HTML templates short-circuit the element-tree pipeline.
  // They arrive with template.kind === 'html' and template.htmlBody set;
  // we render them via a sandboxed iframe so the AI-authored CSS can't
  // leak into player chrome or read anything outside the document.
  if (template?.kind === 'html') {
    return <HtmlTemplateRenderer html={template.htmlBody || ''} />;
  }

  const cfg = template?.config;
  if (!cfg) return <FallbackRenderer message="Missing template config" />;

  switch (cfg.kind) {
    case 'simple':              return <SimpleRenderer cfg={cfg} scale={scale} />;
    case 'menu-board':          return <MenuBoardRenderer cfg={cfg} scale={scale} />;
    case 'split':               return <SplitRenderer cfg={cfg} scale={scale} />;
    case 'countdown':           return <CountdownRenderer cfg={cfg} scale={scale} />;
    case 'employee-spotlight':  return <EmployeeSpotlightRenderer cfg={cfg} scale={scale} />;
    case 'safety':              return <SafetyRenderer cfg={cfg} scale={scale} />;
    case 'canvas':              return <CanvasRenderer cfg={cfg} scale={scale}
                                  animationKey={animationKey} activePageIdx={activePageIdx}
                                  playheadSec={playheadSec} />;
    default:                    return <FallbackRenderer message={`Unsupported kind "${cfg.kind}"`} />;
  }
}

// ─── HTML template renderer (Phase 10.7) ──────────────────────────────────
//
// Drops the AI-authored HTML into a sandboxed iframe via srcdoc. The
// sandbox flags intentionally OMIT 'allow-scripts' — even though our
// server-side sanitizer already strips <script> + on* handlers, the
// iframe is a second layer that ensures any future drift in the sanitizer
// can't expose the player to template-side JS.
//
// Sizing: the iframe fills the parent (100% × 100%). The HTML document
// inside has its own width/height tied to the design aspect (1920×1080
// for 16:9, etc.). CSS transform:scale fits that fixed canvas to the
// player viewport on any TV resolution.
export function HtmlTemplateRenderer({ html }) {
  if (!html) return <FallbackRenderer message="Empty HTML template" />;
  return (
    <iframe
      title="signage-html"
      srcDoc={html}
      sandbox="allow-same-origin"
      style={{
        width: '100%', height: '100%',
        border: 0, display: 'block', background: '#000',
      }}
      // No referrer or feature-policy needed — sandbox + srcdoc isolate this completely.
    />
  );
}

// ─── helpers ──────────────────────────────────────────────────────────────
// "vw"-relative size that scales with the container (works for both full TV
// and 240×135 thumbnails because we use cqw/cqh once container queries are
// available; until then we use vw + a `scale` prop the manager picker can
// use to dial back).
function fs(min, vw, max, scale = 1) {
  // Use cqw if the parent has been declared as a container; falls back to vw.
  return `clamp(${Math.round(min * scale)}px, ${vw * scale}cqw, ${Math.round(max * scale)}px)`;
}

// CapCut Phase 4 — build a CSS `filter` value from a normalized filters
// object. Returns undefined when every channel is identity (no-op) so
// React doesn't add a useless `filter:none` style.
function buildFilterCss(filters) {
  const f = filters || {};
  const parts = [];
  if (f.brightness != null && f.brightness !== 1)  parts.push(`brightness(${f.brightness})`);
  if (f.contrast   != null && f.contrast   !== 1)  parts.push(`contrast(${f.contrast})`);
  if (f.saturate   != null && f.saturate   !== 1)  parts.push(`saturate(${f.saturate})`);
  if (f.hue        != null && f.hue        !== 0)  parts.push(`hue-rotate(${f.hue}deg)`);
  if (f.sepia      != null && f.sepia      > 0)    parts.push(`sepia(${f.sepia})`);
  if (f.grayscale  != null && f.grayscale  > 0)    parts.push(`grayscale(${f.grayscale})`);
  if (f.blur)                                       parts.push(`blur(${f.blur}px)`);
  return parts.length ? parts.join(' ') : undefined;
}

const baseRoot = (bg, fg) => ({
  width: '100%', height: '100%',
  background: bg, color: fg,
  containerType: 'size',           // enables cqw/cqh font sizing
  display: 'flex', flexDirection: 'column',
  overflow: 'hidden',
  fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
});

// ─── simple ───────────────────────────────────────────────────────────────
export function SimpleRenderer({ cfg, scale = 1 }) {
  return (
    <div style={{
      ...baseRoot(cfg.background, cfg.textColor),
      alignItems: 'center', justifyContent: 'center',
      textAlign: 'center', padding: '4cqh 4cqw',
    }}>
      {cfg.imageUrl && (
        <img src={cfg.imageUrl} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }}
          style={{ maxWidth: '50%', maxHeight: '40%', marginBottom: '4cqh', objectFit: 'contain' }} />
      )}
      {cfg.title && (
        <h1 style={{ fontSize: fs(20, 9, 180, scale), fontWeight: 800, margin: 0, letterSpacing: -1, lineHeight: 1.1 }}>
          {cfg.title}
        </h1>
      )}
      {cfg.subtitle && (
        <p style={{ fontSize: fs(10, 3, 60, scale), fontWeight: 400, marginTop: '2cqh', opacity: 0.85 }}>
          {cfg.subtitle}
        </p>
      )}
    </div>
  );
}

// ─── menu-board ───────────────────────────────────────────────────────────
export function MenuBoardRenderer({ cfg, scale = 1 }) {
  // Group items by category if any have one set.
  const grouped = groupByCategory(cfg.items);

  return (
    <div style={{ ...baseRoot(cfg.background, cfg.textColor), padding: '4cqh 5cqw' }}>
      {cfg.title && (
        <h1 style={{
          fontSize: fs(14, 5, 90, scale), fontWeight: 800, margin: 0,
          marginBottom: '3cqh', letterSpacing: -0.5, color: cfg.accent,
        }}>{cfg.title}</h1>
      )}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1.5cqh', overflow: 'hidden' }}>
        {grouped.map((group, gi) => (
          <div key={gi}>
            {group.category && (
              <div style={{
                fontSize: fs(10, 2.4, 36, scale), fontWeight: 700,
                color: cfg.accent, textTransform: 'uppercase',
                letterSpacing: 1.5, marginBottom: '1cqh',
              }}>{group.category}</div>
            )}
            {group.items.map((item, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'baseline', gap: '2cqw',
                marginBottom: '0.6cqh',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: fs(10, 2.6, 40, scale), fontWeight: 600, lineHeight: 1.2 }}>
                    {item.name}
                  </div>
                  {item.description && (
                    <div style={{
                      fontSize: fs(8, 1.6, 22, scale), fontWeight: 400,
                      opacity: 0.75, marginTop: '0.3cqh', lineHeight: 1.3,
                    }}>{item.description}</div>
                  )}
                </div>
                <div style={{
                  fontSize: fs(10, 2.6, 40, scale), fontWeight: 700,
                  color: cfg.accent, whiteSpace: 'nowrap',
                }}>{item.price}</div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function groupByCategory(items) {
  const groups = [];
  let current = null;
  for (const it of items) {
    const cat = it.category || null;
    if (!current || current.category !== cat) {
      current = { category: cat, items: [] };
      groups.push(current);
    }
    current.items.push(it);
  }
  return groups;
}

// ─── split ────────────────────────────────────────────────────────────────
export function SplitRenderer({ cfg, scale = 1 }) {
  const imgFirst = cfg.imageSide === 'left';
  const Image = (
    <div key="img" style={{
      flex: 1,
      backgroundImage: `url("${cfg.imageUrl}")`,
      backgroundSize: 'cover', backgroundPosition: 'center',
    }} />
  );
  const Text = (
    <div key="txt" style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      justifyContent: 'center', padding: '4cqh 5cqw',
    }}>
      <h1 style={{
        fontSize: fs(16, 6, 110, scale), fontWeight: 800, margin: 0,
        letterSpacing: -0.5, lineHeight: 1.1, color: cfg.accent,
      }}>{cfg.headline}</h1>
      {cfg.body && (
        <p style={{
          fontSize: fs(9, 2.4, 36, scale), fontWeight: 400,
          marginTop: '2.5cqh', lineHeight: 1.4, opacity: 0.9,
        }}>{cfg.body}</p>
      )}
    </div>
  );

  return (
    <div style={{ ...baseRoot(cfg.background, cfg.textColor), flexDirection: 'row' }}>
      {imgFirst ? [Image, Text] : [Text, Image]}
    </div>
  );
}

// ─── countdown ────────────────────────────────────────────────────────────
export function CountdownRenderer({ cfg, scale = 1 }) {
  const [now, setNow] = useState(Date.now());
  const targetMs = useRef(Date.parse(cfg.targetIso));

  useEffect(() => {
    const handle = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(handle);
  }, []);

  const remainingMs = Math.max(0, targetMs.current - now);
  const expired = remainingMs === 0;

  return (
    <div style={{
      ...baseRoot(cfg.background, cfg.textColor),
      alignItems: 'center', justifyContent: 'center',
      textAlign: 'center', padding: '4cqh 4cqw',
    }}>
      <div style={{
        fontSize: fs(10, 3, 40, scale), fontWeight: 500,
        opacity: 0.85, marginBottom: '3cqh', letterSpacing: 0.5,
      }}>{cfg.label}</div>
      {expired ? (
        <h1 style={{ fontSize: fs(20, 8, 160, scale), fontWeight: 800, margin: 0, color: cfg.accent }}>
          {cfg.expiredMessage || 'Now!'}
        </h1>
      ) : (
        <div style={{
          fontFamily: "'Space Mono', 'SFMono-Regular', monospace",
          fontSize: fs(24, 11, 200, scale), fontWeight: 700,
          color: cfg.accent, letterSpacing: '0.05em',
        }}>{formatRemaining(remainingMs)}</div>
      )}
    </div>
  );
}

function formatRemaining(ms) {
  const totalSec = Math.floor(ms / 1000);
  const days  = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins  = Math.floor((totalSec % 3600) / 60);
  const secs  = totalSec % 60;
  const pad   = (n) => String(n).padStart(2, '0');
  if (days > 0) return `${days}d ${pad(hours)}:${pad(mins)}:${pad(secs)}`;
  return `${pad(hours)}:${pad(mins)}:${pad(secs)}`;
}

// ─── employee-spotlight ───────────────────────────────────────────────────
export function EmployeeSpotlightRenderer({ cfg, scale = 1 }) {
  return (
    <div style={{
      ...baseRoot(cfg.background, cfg.textColor),
      flexDirection: 'row', alignItems: 'center',
      padding: '4cqh 6cqw', gap: '5cqw',
    }}>
      <div style={{
        width: '32cqh', height: '32cqh',
        minWidth: '32cqh',
        borderRadius: '50%',
        backgroundImage: `url("${cfg.photoUrl}")`,
        backgroundSize: 'cover', backgroundPosition: 'center',
        border: `0.4cqh solid ${cfg.accent}`,
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {cfg.caption && (
          <div style={{
            fontSize: fs(8, 2, 28, scale), fontWeight: 700,
            color: cfg.accent, textTransform: 'uppercase', letterSpacing: 2,
            marginBottom: '2cqh',
          }}>{cfg.caption}</div>
        )}
        <h1 style={{
          fontSize: fs(18, 7, 130, scale), fontWeight: 800, margin: 0,
          letterSpacing: -0.5, lineHeight: 1.1,
        }}>{cfg.name}</h1>
        {cfg.role && (
          <div style={{
            fontSize: fs(10, 3, 40, scale), fontWeight: 500,
            opacity: 0.85, marginTop: '1.5cqh',
          }}>{cfg.role}</div>
        )}
      </div>
    </div>
  );
}

// ─── safety ───────────────────────────────────────────────────────────────
const SAFETY_ICONS = {
  warning:  '⚠',
  info:     'ⓘ',
  check:    '✓',
  cleaning: '✦',
};

export function SafetyRenderer({ cfg, scale = 1 }) {
  return (
    <div style={{
      ...baseRoot(cfg.background, cfg.textColor),
      alignItems: 'center', justifyContent: 'center',
      textAlign: 'center', padding: '4cqh 6cqw',
    }}>
      <div style={{
        fontSize: fs(40, 18, 320, scale),
        lineHeight: 1, marginBottom: '3cqh',
      }}>{SAFETY_ICONS[cfg.iconKind] || SAFETY_ICONS.warning}</div>
      <h1 style={{
        fontSize: fs(16, 6, 110, scale), fontWeight: 800, margin: 0,
        letterSpacing: -0.5, lineHeight: 1.1,
      }}>{cfg.headline}</h1>
      {cfg.body && (
        <p style={{
          fontSize: fs(10, 3, 40, scale), fontWeight: 400,
          marginTop: '2.5cqh', opacity: 0.85, maxWidth: '80%', lineHeight: 1.4,
        }}>{cfg.body}</p>
      )}
    </div>
  );
}

// ─── canvas (freeform) ────────────────────────────────────────────────────
// Plays back an array of absolute-positioned elements. Coordinates and sizes
// are 0–100 percentages of the container, so the same template renders at
// any TV resolution AND at the manager picker's thumbnail size.
//
// Element types: text, image, rect. Schema lives in
// backend/lib/template-schema.js — keep validateElement in sync with the
// fields the renderer actually reads.
export function CanvasRenderer({ cfg, scale = 1, animationKey, activePageIdx, playheadSec }) {
  // Re-evaluate the schedule every minute so the off-hours overlay appears
  // and disappears in real time.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const h = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(h);
  }, []);
  // eslint-disable-next-line no-unused-vars
  const _ = tick;

  // Normalize to a pages[] array regardless of legacy/v11 input shape.
  const pages = Array.isArray(cfg.pages) && cfg.pages.length > 0
    ? cfg.pages
    : [{
        id: 'p-0',
        background: cfg.background,
        backgroundImage: cfg.backgroundImage,
        backgroundFit: cfg.backgroundFit,
        schedule: cfg.schedule,
        elements: cfg.elements || [],
        durationSec: 10,
        transition: { type: 'cut', duration: 600 },
      }];

  // Auto-cycle pages when activePageIdx isn't provided (player mode).
  // In editor preview the caller drives the index — we never auto-cycle.
  const [pageIdx, setPageIdx] = useState(0);
  const driven = activePageIdx != null;
  useEffect(() => {
    if (driven) return;
    if (pages.length < 2) return;
    const dur = (pages[pageIdx]?.durationSec || 10) * 1000;
    const h = setTimeout(() => {
      setPageIdx((i) => (i + 1) % pages.length);
    }, dur);
    return () => clearTimeout(h);
  }, [pageIdx, pages, driven]);

  const effectiveIdx = driven
    ? Math.max(0, Math.min(pages.length - 1, activePageIdx))
    : pageIdx;

  // Track when the current page mounted — kf interpolation uses this as
  // the t=0 reference. Reset whenever effectiveIdx changes.
  const pageStartedAtRef = useRef(performance.now());
  useEffect(() => { pageStartedAtRef.current = performance.now(); }, [effectiveIdx]);

  // ── Transition state: when effectiveIdx changes, hold the OLD page
  // mounted alongside the new one for the new page's transition.duration
  // ms. Animation keyframes do the visual heavy lifting.
  const [prevIdx, setPrevIdx] = useState(null);
  const lastIdxRef = useRef(effectiveIdx);
  useEffect(() => {
    if (effectiveIdx === lastIdxRef.current) return;
    const tx = pages[effectiveIdx]?.transition || { type: 'cut', duration: 600 };
    if (tx.type === 'cut') {
      lastIdxRef.current = effectiveIdx;
      return;
    }
    const from = lastIdxRef.current;
    lastIdxRef.current = effectiveIdx;
    setPrevIdx(from);
    const h = setTimeout(() => setPrevIdx(null), tx.duration);
    return () => clearTimeout(h);
  }, [effectiveIdx, pages]);

  return (
    <div style={{
      width: '100%', height: '100%',
      background: pages[effectiveIdx]?.background || '#ffffff',
      position: 'relative', overflow: 'hidden',
      containerType: 'size',
      fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
    }}>
      <AnimationStyles />
      {prevIdx != null && pages[prevIdx] && (
        <PageLayer
          page={pages[prevIdx]}
          role="leave"
          transition={pages[effectiveIdx]?.transition || { type: 'cut', duration: 600 }}
          animKey={`${animationKey || 0}-${prevIdx}`}
          scale={scale}
          pageStartedAt={pageStartedAtRef.current}
          forcedTime={playheadSec}
        />
      )}
      <PageLayer
        page={pages[effectiveIdx]}
        role="enter"
        transition={prevIdx != null ? (pages[effectiveIdx]?.transition || { type: 'cut', duration: 600 }) : null}
        animKey={`${animationKey || 0}-${effectiveIdx}`}
        scale={scale}
        pageStartedAt={pageStartedAtRef.current}
        forcedTime={playheadSec}
      />
    </div>
  );
}

// ── PageLayer ─────────────────────────────────────────────────────────
// Renders one page's bg + elements as a positioned layer. When `transition`
// is provided, the layer plays the matching enter/leave CSS keyframe so
// two layers can overlap during a page swap.
function PageLayer({ page, role, transition, animKey, scale, pageStartedAt, forcedTime }) {
  const offSchedule = isOffSchedule(page?.schedule);
  if (!page) return null;
  const txClass = transition && transition.type !== 'cut'
    ? `opsora-tx-layer`
    : '';
  const txStyle = transition && transition.type !== 'cut'
    ? {
        animationName: `opsora-tx-${transition.type}-${role}`,
        '--tx-duration': `${transition.duration}ms`,
        // Above the leaving layer.
        zIndex: role === 'enter' ? 2 : 1,
      }
    : (transition ? { zIndex: role === 'enter' ? 2 : 1 } : {});
  return (
    <div className={txClass} style={{
      position: 'absolute', inset: 0,
      background: page.background,
      overflow: 'hidden',
      ...txStyle,
    }}>
      {page.backgroundImage && (
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `url("${page.backgroundImage}")`,
          backgroundSize: page.backgroundFit || 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }} />
      )}
      {(page.elements || [])
        .filter((el) => !el.hidden)
        .map((el) => (
          <KeyframedElement key={`${el.id}-${animKey}`} el={el} scale={scale}
            pageStartedAt={pageStartedAt} forcedTime={forcedTime} />
        ))}
      {offSchedule && <ScheduledOffOverlay schedule={page.schedule} />}
    </div>
  );
}

// Returns true if the current local time is OUTSIDE the template's schedule.
function isOffSchedule(schedule) {
  if (!schedule || !schedule.enabled) return false;
  const now = new Date();
  const day = now.getDay();
  if (Array.isArray(schedule.daysOfWeek) && !schedule.daysOfWeek.includes(day)) return true;
  const minutes = now.getHours() * 60 + now.getMinutes();
  const start = schedule.startMinutes ?? 0;
  const end   = schedule.endMinutes ?? 1440;
  if (start <= end) return minutes < start || minutes >= end;
  // wraps past midnight (e.g. 22:00 → 02:00)
  return minutes < start && minutes >= end;
}

function ScheduledOffOverlay() {
  return (
    <div style={{
      position: 'absolute', inset: 0,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      color: '#fff', textAlign: 'center', padding: '4cqh 4cqw',
      zIndex: 999,
    }}>
      <div style={{ fontSize: '2cqh', color: '#94a3b8',
        textTransform: 'uppercase', letterSpacing: 2, fontWeight: 600, marginBottom: '2cqh',
        fontFamily: '"Space Mono", monospace' }}>
        Scheduled off
      </div>
      <div style={{ fontSize: '4cqh', color: '#e5e7eb', fontWeight: 500, opacity: 0.9 }}>
        This template is outside its play window.
      </div>
    </div>
  );
}

// ─── Shape paths ──────────────────────────────────────────────────────────
// Each path fills a 100×100 viewBox with preserveAspectRatio="none" so the
// shape stretches to the element's bounding box. Add a new shape here AND
// to VALID_SHAPE_KINDS in backend/lib/template-schema.js.
const SHAPE_PATHS = {
  'triangle':     'M50 5 L95 95 L5 95 Z',
  'triangle-down':'M5 5 L95 5 L50 95 Z',
  'star':         'M50 5 L61 38 L96 38 L67 59 L78 92 L50 71 L22 92 L33 59 L4 38 L39 38 Z',
  'starburst':    'M50 0 L57 18 L73 8 L70 28 L92 25 L80 42 L100 50 L80 58 L92 75 L70 72 L73 92 L57 82 L50 100 L43 82 L27 92 L30 72 L8 75 L20 58 L0 50 L20 42 L8 25 L30 28 L27 8 L43 18 Z',
  'heart':        'M50 90 C30 70 5 55 5 35 C5 20 18 5 35 5 C42 5 48 10 50 18 C52 10 58 5 65 5 C82 5 95 20 95 35 C95 55 70 70 50 90 Z',
  'cloud':        'M22 70 Q4 70 4 54 Q4 38 22 36 Q26 18 46 18 Q66 14 70 34 Q88 32 88 54 Q88 70 70 70 Z',
  'lightning':    'M55 5 L20 55 L42 55 L30 95 L80 38 L55 38 Z',
  'moon':         'M62 8 A42 42 0 1 0 62 92 A32 32 0 1 1 62 8 Z',
  'sun':          'M50 28 A22 22 0 1 1 50 72 A22 22 0 1 1 50 28 Z M48 2 L52 2 L52 18 L48 18 Z M48 82 L52 82 L52 98 L48 98 Z M2 48 L18 48 L18 52 L2 52 Z M82 48 L98 48 L98 52 L82 52 Z M14 14 L18 18 L8 28 L4 24 Z M82 82 L86 86 L76 96 L72 92 Z M86 14 L82 18 L92 28 L96 24 Z M14 86 L18 82 L28 92 L24 96 Z',
  'flower':       'M50 10 Q60 10 60 25 Q60 40 50 40 Q40 40 40 25 Q40 10 50 10 Z M50 60 Q60 60 60 75 Q60 90 50 90 Q40 90 40 75 Q40 60 50 60 Z M10 50 Q10 40 25 40 Q40 40 40 50 Q40 60 25 60 Q10 60 10 50 Z M60 50 Q60 40 75 40 Q90 40 90 50 Q90 60 75 60 Q60 60 60 50 Z M50 42 A8 8 0 1 1 50 58 A8 8 0 1 1 50 42 Z',
  'leaf':         'M10 90 Q10 50 50 30 Q90 10 90 50 Q90 90 50 90 Q30 90 10 90 Z',
  'tag':          'M50 8 L92 8 L92 50 L52 92 L8 48 Z M68 22 A6 6 0 1 1 68 34 A6 6 0 1 1 68 22 Z',
  'ribbon':       'M5 20 L75 20 L95 35 L75 50 L5 50 Z M5 56 L65 56 L80 71 L65 86 L5 86 Z',
  'arrow-right':  'M5 35 L60 35 L60 15 L95 50 L60 85 L60 65 L5 65 Z',
  'arrow-left':   'M95 35 L40 35 L40 15 L5 50 L40 85 L40 65 L95 65 Z',
  'arrow-up':     'M35 95 L35 40 L15 40 L50 5 L85 40 L65 40 L65 95 Z',
  'arrow-down':   'M35 5 L35 60 L15 60 L50 95 L85 60 L65 60 L65 5 Z',
  'chevron-right':'M30 10 L42 10 L78 50 L42 90 L30 90 L66 50 Z',
  'chevron-left': 'M70 10 L58 10 L22 50 L58 90 L70 90 L34 50 Z',
  'chevron-up':   'M10 70 L10 58 L50 22 L90 58 L90 70 L50 34 Z',
  'chevron-down': 'M10 30 L10 42 L50 78 L90 42 L90 30 L50 66 Z',
  'hexagon':      'M25 8 L75 8 L97 50 L75 92 L25 92 L3 50 Z',
  'pentagon':     'M50 5 L96 38 L78 92 L22 92 L4 38 Z',
  'octagon':      'M30 5 L70 5 L95 30 L95 70 L70 95 L30 95 L5 70 L5 30 Z',
  'trapezoid':    'M20 10 L80 10 L95 90 L5 90 Z',
  'parallelogram':'M25 10 L95 10 L75 90 L5 90 Z',
  'diamond':      'M50 5 L95 50 L50 95 L5 50 Z',
  'arch':         'M5 95 L5 50 Q5 5 50 5 Q95 5 95 50 L95 95 Z',
  'cross':        'M35 5 L65 5 L65 35 L95 35 L95 65 L65 65 L65 95 L35 95 L35 65 L5 65 L5 35 L35 35 Z',
  'plus':         'M40 12 L60 12 L60 40 L88 40 L88 60 L60 60 L60 88 L40 88 L40 60 L12 60 L12 40 L40 40 Z',
  'check':        'M12 52 L20 44 L40 64 L80 22 L88 30 L40 78 Z',
  'x-mark':       'M22 12 L50 40 L78 12 L88 22 L60 50 L88 78 L78 88 L50 60 L22 88 L12 78 L40 50 L12 22 Z',
  'speech-bubble':'M5 10 L95 10 L95 70 L60 70 L40 95 L40 70 L5 70 Z',
  'chat-bubble':  'M10 15 Q10 5 20 5 L80 5 Q90 5 90 15 L90 60 Q90 70 80 70 L60 70 L42 88 L42 70 L20 70 Q10 70 10 60 Z',
  'gear':         'M50 5 L58 18 L72 14 L72 28 L86 32 L82 46 L94 54 L84 64 L90 78 L76 80 L72 94 L58 88 L50 95 L42 88 L28 94 L24 80 L10 78 L16 64 L6 54 L18 46 L14 32 L28 28 L28 14 L42 18 Z M50 32 A18 18 0 1 1 50 68 A18 18 0 1 1 50 32 Z',
};

// Build the universal effects style (applied to every element type) — the
// outer wrapper handles opacity + rotation + box-shadow so every kind picks
// them up uniformly.
function effectsStyle(el) {
  const transform = el.rotation ? `rotate(${el.rotation}deg)` : undefined;
  const boxShadow = el.shadow?.enabled
    ? `${el.shadow.offsetX}px ${el.shadow.offsetY}px ${el.shadow.blur}px ${el.shadow.color}`
    : undefined;
  return {
    opacity: el.opacity ?? 1,
    transform,
    boxShadow,
  };
}

function CanvasElement({ el, scale }) {
  // Outer wrapper: position + universal effects + animation. The inner
  // element fills the wrapper via `inset: 0`. This split means the
  // animation class can run keyframes that touch transform/opacity without
  // fighting the static layout.
  const animation = el.animation;
  const isMarquee = animation?.type === 'marquee';
  const animClass = getAnimationClass(animation);
  const animStyle = getAnimationStyle(animation);

  const wrapperStyle = {
    position: 'absolute',
    left: `${el.x}%`, top: `${el.y}%`,
    width: `${el.w}%`, height: `${el.h}%`,
    // Clip child overflow to the bbox. Without this, replaced elements
    // (video, img, iframe) can render at their intrinsic natural size
    // and spill outside their wrapper — the box on screen looks small
    // but the actual video covers half the canvas. Selection chrome
    // lives in a separate overlay layer in the editor, so clipping
    // here doesn't hide handles.
    overflow: 'hidden',
    ...effectsStyle(el),
    ...(animStyle || {}),
  };

  // Children render with absolute inset:0 so they fill the wrapper. Each
  // type-block below uses `inner` (computed via positionStyle === inset:0).
  const positionStyle = { position: 'absolute', inset: 0 };

  let inner = null;
  if (el.type === 'text') {
    const textShadow = el.textShadow?.enabled
      ? `${el.textShadow.offsetX}px ${el.textShadow.offsetY}px ${el.textShadow.blur}px ${el.textShadow.color}`
      : undefined;
    const justifyContent =
      el.align === 'center' ? 'center' :
      el.align === 'right'  ? 'flex-end' : 'flex-start';
    const alignItems =
      el.verticalAlign === 'top'    ? 'flex-start' :
      el.verticalAlign === 'bottom' ? 'flex-end'   : 'center';
    const textBase = {
      ...positionStyle,
      color: el.color,
      fontFamily: el.fontFamily ? `"${el.fontFamily}", system-ui, sans-serif` : undefined,
      fontWeight: el.fontWeight,
      fontStyle: el.italic ? 'italic' : 'normal',
      textDecoration: el.underline ? 'underline' : 'none',
      textAlign: el.align,
      textTransform: el.textTransform,
      letterSpacing: el.letterSpacing ? `${el.letterSpacing}em` : undefined,
      textShadow,
      display: 'flex',
      alignItems,
      justifyContent,
      lineHeight: el.lineHeight ?? 1.15,
      whiteSpace: isMarquee ? 'nowrap' : 'pre-wrap',
      wordBreak: 'break-word',
      overflow: 'hidden',
    };
    // PowerPoint-style auto-shrink: when text overflows the box, drop
    // font size until it fits. Default on for newly-created text. The
    // user can flip it off in the property panel to keep the fixed size
    // (in which case overflow clips, same as the original behavior).
    inner = el.autoFit !== false && !isMarquee
      ? <AutoFitText style={textBase} fontSize={el.fontSize}>{el.text}</AutoFitText>
      : <div style={{ ...textBase, fontSize: `${el.fontSize}cqh` }}>{el.text}</div>;
  }

  if (el.type === 'image') {
    inner = (
      <div style={{
        ...positionStyle,
        backgroundImage: `url("${el.url}")`,
        backgroundSize: el.fit,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        // No background color — transparent PNGs / stickers must show
        // the canvas bg through their alpha channel.
        borderRadius: el.borderRadius ? `${el.borderRadius}px` : undefined,
        filter: buildFilterCss(el.filters),
      }} />
    );
  }

  if (el.type === 'video') {
    inner = <VideoEl el={el} positionStyle={positionStyle} />;
  }

  if (el.type === 'rect') {
    inner = (
      <div style={{
        ...positionStyle,
        background: el.fill,
        border: el.borderWidth ? `${el.borderWidth}px solid ${el.borderColor}` : 'none',
        borderRadius: `${el.borderRadius}px`,
      }} />
    );
  }

  if (el.type === 'circle') {
    inner = (
      <div style={{
        ...positionStyle,
        background: el.fill,
        border: el.borderWidth ? `${el.borderWidth}px solid ${el.borderColor}` : 'none',
        borderRadius: '50%',
      }} />
    );
  }

  if (el.type === 'line') {
    inner = (
      <div style={{ ...positionStyle, display: 'flex', alignItems: 'center' }}>
        <div style={{ width: '100%', height: el.thickness, background: el.color }} />
      </div>
    );
  }

  if (el.type === 'qr') {
    // Use api.qrserver.com — free, no API key, always scannable. Color
    // params take hex without the # prefix. Background works as solid color
    // around the QR; the wrapper div applies the user's chosen bg color.
    const fgHex = (el.fg || '#000000').replace('#', '');
    const bgHex = (el.bg || '#ffffff').replace('#', '');
    const url = `https://api.qrserver.com/v1/create-qr-code/?size=600x600` +
                `&data=${encodeURIComponent(el.data)}` +
                `&color=${fgHex}&bgcolor=${bgHex}&qzone=2&margin=0`;
    inner = (
      <div style={{ ...positionStyle, background: el.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img src={url} alt="QR code"
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }} />
      </div>
    );
  }

  if (el.type === 'adjustment') {
    // Pro Phase A — apply filter chain to everything visible beneath this
    // layer via backdrop-filter. Transparent fill; outline only shown in
    // the editor (where the selection rectangle takes care of it).
    inner = (
      <div style={{
        ...positionStyle,
        backgroundColor: 'transparent',
        backdropFilter: buildFilterCss(el.filters),
        WebkitBackdropFilter: buildFilterCss(el.filters),
      }} />
    );
  }

  if (el.type === 'clock')       inner = <ClockEl       el={el} positionStyle={positionStyle} />;
  if (el.type === 'date')        inner = <DateEl        el={el} positionStyle={positionStyle} />;
  if (el.type === 'weather')     inner = <WeatherEl     el={el} positionStyle={positionStyle} />;
  if (el.type === 'countdown')   inner = <CountdownEl   el={el} positionStyle={positionStyle} />;
  if (el.type === 'prayertimes') inner = <PrayerTimesEl el={el} positionStyle={positionStyle} />;
  if (el.type === 'kpicard')     inner = <KpiCardEl     el={el} positionStyle={positionStyle} />;
  if (el.type === 'shifts')      inner = <ShiftsEl      el={el} positionStyle={positionStyle} />;
  if (el.type === 'chart')       inner = <ChartEl       el={el} positionStyle={positionStyle} />;

  if (el.type === 'youtube') {
    // Loop requires playlist=<id>&loop=1 (YouTube quirk).
    const params = new URLSearchParams({
      autoplay: el.autoplay ? '1' : '0',
      mute:     el.mute ? '1' : '0',
      controls: el.controls ? '1' : '0',
      loop:     el.loop ? '1' : '0',
      playlist: el.loop ? el.videoId : '',
      modestbranding: '1',
      rel: '0',
      playsinline: '1',
      // Strip every overlay YouTube still draws when controls=0:
      iv_load_policy: '3',     // no annotations
      disablekb: '1',          // no keyboard shortcuts
      fs: '0',                 // no fullscreen button
      cc_load_policy: '0',     // no closed captions overlay
      showinfo: '0',           // legacy, still respected by some embeds
    });
    // We wrap the iframe in an overflow-clipping div and oversize +
    // shift the iframe up/down so the persistent YouTube "title bar"
    // (uploader name + share/watch-later icons) and any bottom progress
    // chrome are pushed off-screen and clipped. Net effect: the user
    // sees only the video pixels, no YouTube branding.
    //
    // Numbers tuned for the standard YT embed chrome at typical sizes:
    //   - title bar  ≈ 12% of height at top
    //   - bottom bar ≈ 12% of height at bottom (only on hover/pause)
    // Scaling 1.30x vertically + offsetting -15% top hides both bands
    // while still showing the full visible video frame.
    const src = `https://www.youtube-nocookie.com/embed/${el.videoId}?${params.toString()}`;
    inner = (
      <div style={{
        ...positionStyle,
        overflow: 'hidden',
        background: '#000',
      }}>
        <iframe
          key={el.videoId + el.mute + el.loop}
          src={src}
          title=""
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{
            position: 'absolute',
            top: '-15%', left: '-15%',
            width: '130%', height: '130%',
            background: '#000', border: 'none', display: 'block',
            pointerEvents: 'none',   // can't click through to YT UI anyway
          }}
        />
      </div>
    );
  }

  if (el.type === 'shape') {
    // Renders an SVG path that fills the element's bounding box.
    // Stroke width is in viewBox units (out of 100) so it scales with size.
    const path = SHAPE_PATHS[el.shapeKind] || SHAPE_PATHS.triangle;
    inner = (
      <svg style={positionStyle} viewBox="0 0 100 100" preserveAspectRatio="none">
        <path d={path}
          fill={el.fill}
          stroke={el.borderWidth ? el.borderColor : 'none'}
          strokeWidth={el.borderWidth}
          vectorEffect="non-scaling-stroke" />
      </svg>
    );
  }

  if (el.type === 'subscreen') {
    // Recursively render the embedded canvas template (or a placeholder).
    if (!el.inlineConfig) {
      inner = (
        <div style={{
          ...positionStyle, background: 'rgba(255,255,255,0.05)',
          border: '1px dashed rgba(255,255,255,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'rgba(255,255,255,0.5)', fontSize: '2cqh', fontFamily: '"Space Mono", monospace',
        }}>{el.label || 'Empty region'}</div>
      );
    } else {
      inner = (
        <div style={{ ...positionStyle, overflow: 'hidden' }}>
          <CanvasRenderer cfg={el.inlineConfig} scale={scale} />
        </div>
      );
    }
  }

  if (inner == null) return null;

  // Wrap inner with animation if requested. Marquee + scroll swap the
  // layout: the wrapper becomes the overflow clip, and the inner content
  // rides on a .opsora-*-track that translates across the box. Scroll
  // supports all 4 directions; marquee is the legacy right→left scroll.
  const isScroll = animation?.type === 'scroll';
  if (isMarquee || isScroll) {
    const dir = animation?.direction || (isMarquee ? 'left' : 'up');
    const trackClass = isMarquee
      ? 'opsora-marquee-track'
      : `opsora-scroll-track-${dir}`;
    const clipClass = isMarquee ? 'opsora-marquee-clip' : 'opsora-scroll-clip';
    return (
      <div style={wrapperStyle} className={clipClass}>
        <div className={trackClass} style={animStyle}>
          {scrollInline(inner, dir, isMarquee)}
        </div>
      </div>
    );
  }
  if (animClass || animStyle) {
    return <div className={animClass} style={wrapperStyle}>{inner}</div>;
  }
  // No animation: render inner directly with the wrapper's position styles
  // merged in. Saves one DOM node for the common case (existing templates
  // that haven't opted into animation).
  return <div style={wrapperStyle}>{inner}</div>;
}

// Used by marquee / scroll — strips absolute positioning off the inner
// element so it flows inline-block along the moving track. Horizontal
// tracks need inline-block (single line); vertical tracks need block so
// the height auto-fits and translateY does what we want.
function scrollInline(inner, dir, isMarquee) {
  const horizontal = isMarquee || dir === 'left' || dir === 'right';
  return (
    <div style={{
      position: 'relative',
      display: horizontal ? 'inline-block' : 'block',
      whiteSpace: horizontal ? 'nowrap' : 'normal',
      height: horizontal ? '100%' : 'auto',
      width:  horizontal ? 'auto'  : '100%',
    }}>
      {inner}
    </div>
  );
}

// ─── Video element with CapCut-style trim / speed / volume / mirror ──
// We manage the <video> via a ref so we can imperatively set playbackRate
// (no React attribute), volume, and react to timeupdate to enforce the
// trim window. mirror / flipY apply CSS transforms — no re-encoding.
// PowerPoint-style "shrink text on overflow" — measures the rendered text
// after layout and binary-searches the largest font size that still fits
// inside the box. Cheap to run (≤ ~6 iterations) so it's safe per text
// change. Falls back to the declared fontSize when nothing overflows.
function AutoFitText({ style, fontSize, children }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    // Reset, then shrink in 5% steps with a hard cap.
    let size = fontSize;
    node.style.fontSize = `${size}cqh`;
    let attempts = 0;
    while (attempts < 24 &&
           (node.scrollHeight > node.clientHeight ||
            node.scrollWidth  > node.clientWidth)) {
      size = size * 0.92;
      if (size < 0.4) break;
      node.style.fontSize = `${size}cqh`;
      attempts++;
    }
  });
  return <div ref={ref} style={{ ...style, fontSize: `${fontSize}cqh` }}>{children}</div>;
}

function VideoEl({ el, positionStyle }) {
  const videoRef = useRef(null);
  // Apply playback config whenever it changes. Volume + playbackRate are
  // imperative-only (React doesn't expose them as props).
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = el.speed || 1;
    v.volume = el.muted ? 0 : (el.volume ?? 1);
    v.muted = el.muted || (el.volume ?? 1) === 0;
    // Seek to trimStart on (re)mount or when trimStart changes.
    if (el.trimStart && Math.abs(v.currentTime - el.trimStart) > 0.05) {
      try { v.currentTime = el.trimStart; } catch {}
    }
  }, [el.speed, el.volume, el.muted, el.trimStart]);

  // Enforce trim window: when currentTime crosses trimEnd, loop back to
  // trimStart (if loop is on) or pause. Browser's native `loop` doesn't
  // know about trim, so we shim it.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const start = el.trimStart || 0;
    const end = el.trimEnd;
    function onTime() {
      if (end != null && v.currentTime >= end) {
        if (el.loop) {
          try { v.currentTime = start; v.play().catch(() => {}); } catch {}
        } else {
          v.pause();
        }
      }
    }
    v.addEventListener('timeupdate', onTime);
    return () => v.removeEventListener('timeupdate', onTime);
  }, [el.trimStart, el.trimEnd, el.loop]);

  const flipX = el.mirror ? -1 : 1;
  const flipY = el.flipY  ? -1 : 1;
  // Always include translateZ(0) so the browser hoists the video into
  // its own GPU compositing layer. Without that hint, cheap TV browsers
  // (ONN FHD, Fire Stick gen-1) often composite the video on the CPU,
  // which destroys playback at any resolution above 480p. The flip
  // matrix composes onto the same transform so we don't lose hardware
  // promotion when the user mirrors.
  const transform = `translateZ(0) scale(${flipX}, ${flipY})`;
  const filterCss = buildFilterCss(el.filters);

  return (
    <video
      ref={videoRef}
      key={el.url}                       // force remount when source changes
      src={el.url}
      autoPlay
      muted={el.muted || (el.volume ?? 1) === 0}
      loop={el.loop && !el.trimEnd}       // browser loop only when no trim
      playsInline
      // preload=auto tells the TV browser to fully buffer before play.
      // On a slow uplink to a TV LAN, on-demand range requests stutter
      // visibly; auto smooths it out by front-loading the fetch.
      preload="auto"
      // Strip TV-side overhead the player never needs: AirPlay /
      // Chromecast remote control, picture-in-picture button, and the
      // standard hover controls (no one's hovering a TV).
      disablePictureInPicture
      disableRemotePlayback
      controls={false}
      style={{
        ...positionStyle,
        // Replaced elements (video) often ignore inset:0 and fall back
        // to their intrinsic dimensions — making the video balloon to
        // its natural size and overflow the wrapper. Setting explicit
        // 100%/100% forces it to track the bbox. Also default the fit
        // to 'contain' for any legacy element whose fit is undefined.
        width: '100%',
        height: '100%',
        objectFit: el.fit || 'contain',
        display: 'block',
        backgroundColor: '#000',
        transform,
        // will-change tells the compositor "this layer's transform will
        // animate, please keep it on the GPU." Combined with the
        // translateZ above, this is what unsticks cheap TV browsers.
        willChange: 'transform',
        // CapCut Phase 4 — apply the same filter chain to video, but
        // only set the CSS property when there's actually a filter. CSS
        // `filter` (even no-op chains) forces CPU compositing on weak
        // GPUs and tanks playback. Skip entirely when unused.
        ...(filterCss ? { filter: filterCss } : null),
      }}
    />
  );
}

// ─── Clock widget — updates every second ──────────────────────────────────
function ClockEl({ el, positionStyle }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const h = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(h);
  }, []);
  return (
    <div style={textWidgetStyle(el, positionStyle)}>
      {formatClock(now, el.format)}
    </div>
  );
}

function formatClock(d, fmt) {
  const h24 = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  const s = String(d.getSeconds()).padStart(2, '0');
  if (fmt === '24h')  return `${String(h24).padStart(2, '0')}:${m}`;
  if (fmt === '24hs') return `${String(h24).padStart(2, '0')}:${m}:${s}`;
  const ampm = h24 >= 12 ? 'PM' : 'AM';
  const h12 = ((h24 + 11) % 12) + 1;
  if (fmt === '12hs') return `${h12}:${m}:${s} ${ampm}`;
  return `${h12}:${m} ${ampm}`;
}

// ─── Date widget — updates every minute ──────────────────────────────────
function DateEl({ el, positionStyle }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const h = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(h);
  }, []);
  return (
    <div style={textWidgetStyle(el, positionStyle)}>
      {formatDate(now, el.format)}
    </div>
  );
}

function formatDate(d, fmt) {
  const days  = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const moShort = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const moLong  = ['January','February','March','April','May','June',
                   'July','August','September','October','November','December'];
  const day = days[d.getDay()];
  if (fmt === 'short')  return `${moShort[d.getMonth()]} ${d.getDate()}`;
  if (fmt === 'medium') return `${moShort[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  if (fmt === 'long')   return `${moLong[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  if (fmt === 'day')    return day;
  return `${day}, ${moLong[d.getMonth()]} ${d.getDate()}`;
}

// ─── Weather widget — Open-Meteo, refresh every 15 min ──────────────────
// No API key required. Caches in module-level Map so multiple weather
// elements with the same coords share one fetch.
const _weatherCache = new Map();   // key → { data, fetchedAt }
const WEATHER_TTL_MS = 15 * 60 * 1000;

function WeatherEl({ el, positionStyle }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const key = `${el.lat.toFixed(3)},${el.lon.toFixed(3)},${el.units}`;
    const cached = _weatherCache.get(key);
    if (cached && Date.now() - cached.fetchedAt < WEATHER_TTL_MS) {
      setData(cached.data);
    } else {
      const tempUnit = el.units === 'celsius' ? 'celsius' : 'fahrenheit';
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${el.lat}&longitude=${el.lon}` +
                  `&current=temperature_2m,weather_code&daily=temperature_2m_max,temperature_2m_min` +
                  `&temperature_unit=${tempUnit}&forecast_days=1&timezone=auto`;
      fetch(url)
        .then((r) => r.json())
        .then((d) => {
          if (cancelled) return;
          const norm = {
            temp: Math.round(d?.current?.temperature_2m ?? 0),
            code: d?.current?.weather_code ?? 0,
            high: Math.round(d?.daily?.temperature_2m_max?.[0] ?? 0),
            low:  Math.round(d?.daily?.temperature_2m_min?.[0] ?? 0),
          };
          _weatherCache.set(key, { data: norm, fetchedAt: Date.now() });
          setData(norm);
        })
        .catch(() => { /* network blip — show fallback */ });
    }
    // Refresh interval inside the component so components mounted later still
    // pick up fresh data.
    const h = setInterval(() => {
      const c = _weatherCache.get(key);
      if (c) setData(c.data);
    }, 60_000);
    return () => { cancelled = true; clearInterval(h); };
  }, [el.lat, el.lon, el.units]);

  if (!data) {
    return <div style={textWidgetStyle(el, positionStyle)}>—°</div>;
  }
  const cond = weatherCodeToText(data.code);
  const symbol = el.units === 'celsius' ? '°C' : '°F';
  let label;
  if (el.display === 'temp') label = `${data.temp}${symbol}`;
  else if (el.display === 'temp-cond') label = `${data.temp}${symbol} ${cond}`;
  else label = `${data.temp}${symbol} ${cond} · ↓ ${data.low} / ↑ ${data.high}`;
  return <div style={textWidgetStyle(el, positionStyle)}>{label}</div>;
}

// Open-Meteo WMO code → human label (subset that covers ~99% of cases).
function weatherCodeToText(code) {
  if (code === 0) return 'Clear';
  if (code <= 3) return 'Partly cloudy';
  if (code === 45 || code === 48) return 'Foggy';
  if (code >= 51 && code <= 57) return 'Drizzle';
  if (code >= 61 && code <= 67) return 'Rain';
  if (code >= 71 && code <= 77) return 'Snow';
  if (code >= 80 && code <= 82) return 'Showers';
  if (code >= 85 && code <= 86) return 'Snow showers';
  if (code >= 95) return 'Thunderstorm';
  return 'Cloudy';
}

// Shared style for text-based widgets (clock/date/weather).
function textWidgetStyle(el, positionStyle) {
  const justify = el.align === 'center' ? 'center'
                : el.align === 'right'  ? 'flex-end' : 'flex-start';
  return {
    ...positionStyle,
    color: el.color,
    fontFamily: el.fontFamily ? `"${el.fontFamily}", system-ui, sans-serif` : undefined,
    fontSize: `${el.fontSize}cqh`,
    fontWeight: 700,
    textAlign: el.align,
    display: 'flex', alignItems: 'center', justifyContent: justify,
    overflow: 'hidden', whiteSpace: 'nowrap',
    lineHeight: 1,
  };
}

// ─── Countdown widget ─────────────────────────────────────────────────────
// Ticks every second to a target Unix-ms. Renders Days · Hours · Min · Sec
// (with optional label above). When the target passes, swaps to expiredText.
function CountdownEl({ el, positionStyle }) {
  const [, force] = useState(0);
  useEffect(() => {
    const h = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(h);
  }, []);
  const target = Number(el.targetAt) || 0;
  const now = Date.now();
  const expired = target <= now;
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins  = Math.floor((diff % 3600000)  / 60000);
  const secs  = Math.floor((diff % 60000)    / 1000);
  const pad = (n) => String(n).padStart(2, '0');

  const justify = el.align === 'center' ? 'center'
                : el.align === 'right'  ? 'flex-end' : 'flex-start';
  if (expired) {
    return (
      <div style={{
        ...positionStyle, display: 'flex', flexDirection: 'column',
        alignItems: justify === 'center' ? 'center'
                  : justify === 'flex-end' ? 'flex-end' : 'flex-start',
        justifyContent: 'center', textAlign: el.align, overflow: 'hidden',
        color: el.color,
        fontFamily: el.fontFamily ? `"${el.fontFamily}", system-ui, sans-serif` : undefined,
      }}>
        <div style={{ fontSize: `${el.fontSize * 1.4}cqh`, fontWeight: 800,
          color: el.accentColor }}>{el.expiredText || 'Time’s up!'}</div>
      </div>
    );
  }

  const showDays = el.display === 'dhms' || el.display === 'days';
  const showHMS  = el.display !== 'days';
  const showMS   = el.display !== 'days';

  // Each segment: big number + small label underneath.
  const Seg = ({ value, label }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
      minWidth: '4cqh', padding: '0 1cqw' }}>
      <div style={{ fontSize: `${el.fontSize * 1.6}cqh`, fontWeight: 800,
        color: el.accentColor || el.color, lineHeight: 1,
        fontFamily: '"Space Mono", monospace, ui-monospace', }}>
        {value}
      </div>
      <div style={{ fontSize: `${el.fontSize * 0.5}cqh`, fontWeight: 600,
        opacity: 0.7, marginTop: '0.6cqh', textTransform: 'uppercase',
        letterSpacing: '0.15em' }}>{label}</div>
    </div>
  );

  return (
    <div style={{
      ...positionStyle, display: 'flex', flexDirection: 'column',
      alignItems: justify === 'center' ? 'center'
                : justify === 'flex-end' ? 'flex-end' : 'flex-start',
      justifyContent: 'center', overflow: 'hidden',
      color: el.color, textAlign: el.align,
      fontFamily: el.fontFamily ? `"${el.fontFamily}", system-ui, sans-serif` : undefined,
    }}>
      {el.label && (
        <div style={{ fontSize: `${el.fontSize * 0.55}cqh`, opacity: 0.75,
          textTransform: 'uppercase', letterSpacing: '0.2em', fontWeight: 600,
          marginBottom: '1cqh' }}>{el.label}</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: '0.5cqw' }}>
        {showDays && <Seg value={days} label="Days" />}
        {showHMS  && (el.display === 'dhms' || el.display === 'hms') &&
          <Seg value={pad(hours)} label="Hours" />}
        {showMS   && <Seg value={pad(mins)}  label="Min" />}
        {showMS   && <Seg value={pad(secs)}  label="Sec" />}
      </div>
    </div>
  );
}

// ─── Prayer times widget ──────────────────────────────────────────────────
// Fetches 5 daily prayer times from api.aladhan.com (free, no API key).
// Refreshes once at midnight and on remount. Cached per city+country+date.
const _prayerCache = new Map();   // key → { data, dateIso, fetchedAt }
function PrayerTimesEl({ el, positionStyle }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [, force] = useState(0);
  const dateIso = new Date().toISOString().slice(0, 10);
  const key = `${el.city}|${el.country}|${el.method}|${dateIso}`;
  useEffect(() => {
    const cached = _prayerCache.get(key);
    if (cached) { setData(cached.data); return; }
    let cancelled = false;
    (async () => {
      try {
        const url = `https://api.aladhan.com/v1/timingsByCity` +
          `?city=${encodeURIComponent(el.city)}` +
          `&country=${encodeURIComponent(el.country)}` +
          `&method=${encodeURIComponent(el.method)}`;
        const r = await fetch(url);
        const j = await r.json();
        if (cancelled) return;
        const t = j?.data?.timings;
        if (!t) throw new Error('No timings in response');
        const out = {
          Fajr: t.Fajr, Dhuhr: t.Dhuhr, Asr: t.Asr,
          Maghrib: t.Maghrib, Isha: t.Isha,
        };
        _prayerCache.set(key, { data: out, dateIso, fetchedAt: Date.now() });
        setData(out);
      } catch (e) {
        if (!cancelled) setErr(e.message || 'Prayer fetch failed');
      }
    })();
    return () => { cancelled = true; };
  }, [key, el.city, el.country, el.method]);

  // Tick once per minute so the "next prayer" highlight stays current.
  useEffect(() => {
    const h = setInterval(() => force((n) => n + 1), 60_000);
    return () => clearInterval(h);
  }, []);

  if (err) {
    return <div style={{ ...positionStyle, color: el.color, padding: '2cqh',
      fontSize: `${el.fontSize}cqh`, fontFamily: el.fontFamily }}>
      Prayer times unavailable
    </div>;
  }
  if (!data) {
    return <div style={{ ...positionStyle, color: el.color, padding: '2cqh',
      fontSize: `${el.fontSize}cqh`, fontFamily: el.fontFamily, opacity: 0.6 }}>
      Loading prayer times…
    </div>;
  }

  // Format "HH:MM" (24h) → 12h or 24h depending on widget setting.
  const fmt = (hhmm) => {
    if (el.format === '24h') return hhmm;
    const [h, m] = String(hhmm || '').split(':').map(Number);
    if (!Number.isFinite(h)) return hhmm;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  // Next prayer: find first whose time > now (HH:MM compared lexically).
  const now = new Date();
  const nowHM = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const order = ['Fajr', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];
  let nextKey = null;
  for (const k of order) { if (data[k] && data[k] > nowHM) { nextKey = k; break; } }

  const isVertical = el.layout === 'vertical';
  const isCompact  = el.layout === 'compact';
  const rowStyle = isVertical
    ? { display: 'flex', flexDirection: 'column', gap: '1.2cqh' }
    : { display: 'flex', flexDirection: 'row', gap: '2cqw', justifyContent: 'space-around' };

  return (
    <div style={{
      ...positionStyle,
      color: el.color,
      fontFamily: el.fontFamily ? `"${el.fontFamily}", system-ui, sans-serif` : undefined,
      padding: '2cqh 2cqw',
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      overflow: 'hidden',
    }}>
      <div style={rowStyle}>
        {order.map((k) => {
          const isNext = el.showNext && k === nextKey;
          return (
            <div key={k} style={{
              display: 'flex', flexDirection: isVertical ? 'row' : 'column',
              alignItems: 'center', justifyContent: isVertical ? 'space-between' : 'center',
              gap: isVertical ? '1cqw' : '0.4cqh',
              padding: isCompact ? '0' : '0.6cqh 0.6cqw',
              borderRadius: isCompact ? 0 : '0.6cqh',
              background: isNext ? `${el.accentColor || '#10b981'}25` : 'transparent',
              border: isNext ? `1.5px solid ${el.accentColor || '#10b981'}` : '1.5px solid transparent',
              flex: isVertical ? '0 0 auto' : 1,
            }}>
              <div style={{ fontSize: `${el.fontSize * 0.7}cqh`, opacity: 0.8,
                fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{k}</div>
              <div style={{ fontSize: `${el.fontSize}cqh`, fontWeight: 800,
                color: isNext ? (el.accentColor || '#10b981') : el.color,
                fontFamily: '"Space Mono", monospace, ui-monospace' }}>
                {fmt(data[k])}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── KPI card widget ──────────────────────────────────────────────────────
// Fetches /screens/me/kpis/sales and renders the requested metric. Real
// data plumbing comes when the POS integration ships — for now the endpoint
// returns deterministic mock numbers seeded by date+business so the same
// screen shows the same number all day.
const _kpiCache = { data: null, fetchedAt: 0 };
function KpiCardEl({ el, positionStyle }) {
  const [data, setData] = useState(_kpiCache.data);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      // Reuse cached value if <5 min old to spare the backend.
      if (_kpiCache.data && Date.now() - _kpiCache.fetchedAt < 5 * 60_000) {
        setData(_kpiCache.data); return;
      }
      try {
        const token = localStorage.getItem('opsora-screen-token');
        const r = await fetch('/screens/me/kpis/sales', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!r.ok) throw new Error(`KPI ${r.status}`);
        const j = await r.json();
        if (cancelled) return;
        _kpiCache.data = j;
        _kpiCache.fetchedAt = Date.now();
        setData(j);
      } catch {
        // Show a sensible placeholder in the editor preview where there's
        // no screen token — values look real so the widget reads correctly.
        const placeholder = {
          sales:     { value: 4250, currency: 'USD' },
          orders:    { value: 132 },
          avgticket: { value: 32.20, currency: 'USD' },
          laborpct:  { value: 28.4, unit: '%' },
          topitem:   { value: 'Chicken Shawarma' },
        };
        if (!cancelled) setData(placeholder);
      }
    };
    load();
    const h = setInterval(load, 5 * 60_000);
    return () => { cancelled = true; clearInterval(h); };
  }, []);

  const labels = {
    sales: 'Sales today', orders: 'Orders', avgticket: 'Avg ticket',
    laborpct: 'Labor %',  topitem: 'Top item',
  };
  const fmt = (m, v) => {
    if (v == null) return '—';
    if (m === 'sales' || m === 'avgticket')
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD',
        maximumFractionDigits: m === 'sales' ? 0 : 2 }).format(v);
    if (m === 'laborpct') return `${v}%`;
    return String(v);
  };
  const metric = el.metric || 'sales';
  const value = data?.[metric]?.value;
  const display = fmt(metric, value);
  const label = el.label || labels[metric] || metric;
  const trendSymbol = el.trend === 'up' ? '▲' : el.trend === 'down' ? '▼' : el.trend === 'flat' ? '→' : '';
  const trendColor  = el.trend === 'up' ? '#10b981' : el.trend === 'down' ? '#ef4444' : '#94a3b8';

  return (
    <div style={{
      ...positionStyle,
      background: el.background,
      borderRadius: '1cqh',
      padding: '2cqh 2cqw',
      display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      overflow: 'hidden',
      borderLeft: `0.8cqh solid ${el.accentColor || '#7c3aed'}`,
      color: el.color,
      fontFamily: el.fontFamily ? `"${el.fontFamily}", system-ui, sans-serif` : undefined,
    }}>
      <div style={{ fontSize: '1.4cqh', textTransform: 'uppercase',
        letterSpacing: '0.18em', opacity: 0.7, fontWeight: 700 }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.8cqw',
        marginTop: '0.5cqh' }}>
        <div style={{ fontSize: '6cqh', fontWeight: 800, lineHeight: 1,
          color: el.color, fontFamily: '"Space Mono", monospace, ui-monospace' }}>
          {display}
        </div>
        {trendSymbol && (
          <span style={{ fontSize: '1.6cqh', color: trendColor, fontWeight: 700 }}>
            {trendSymbol}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Upcoming-shifts widget ───────────────────────────────────────────────
// Pulls real schedule data via /screens/me/shifts/upcoming.
const _shiftsCache = { data: null, fetchedAt: 0, scope: null };
function ShiftsEl({ el, positionStyle }) {
  const [data, setData] = useState(_shiftsCache.scope === el.scope ? _shiftsCache.data : null);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (_shiftsCache.scope === el.scope && _shiftsCache.data &&
          Date.now() - _shiftsCache.fetchedAt < 5 * 60_000) {
        setData(_shiftsCache.data); return;
      }
      try {
        const token = localStorage.getItem('opsora-screen-token');
        const url = `/screens/me/shifts/upcoming?count=${el.count}&scope=${el.scope}`;
        const r = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!r.ok) throw new Error(`Shifts ${r.status}`);
        const j = await r.json();
        if (cancelled) return;
        _shiftsCache.data = j;
        _shiftsCache.fetchedAt = Date.now();
        _shiftsCache.scope = el.scope;
        setData(j);
      } catch {
        // Editor preview / unauthenticated → show sample shifts.
        const sample = { shifts: [
          { name: 'Sarah K.',  date: '2026-05-17', start: '09:00', end: '14:00', branch: 'Round Rock', branchColor: '#0F766E' },
          { name: 'Ahmed M.',  date: '2026-05-17', start: '11:00', end: '17:00', branch: 'Round Rock', branchColor: '#0F766E' },
          { name: 'Jose R.',   date: '2026-05-17', start: '14:00', end: '22:00', branch: 'Round Rock', branchColor: '#0F766E' },
          { name: 'Emily T.',  date: '2026-05-18', start: '08:00', end: '13:00', branch: 'Round Rock', branchColor: '#0F766E' },
          { name: 'Marco V.',  date: '2026-05-18', start: '15:00', end: '23:00', branch: 'Round Rock', branchColor: '#0F766E' },
        ].slice(0, el.count) };
        if (!cancelled) setData(sample);
      }
    };
    load();
    const h = setInterval(load, 5 * 60_000);
    return () => { cancelled = true; clearInterval(h); };
  }, [el.count, el.scope]);

  const shifts = data?.shifts || [];
  const fmtTime = (t) => {
    if (!t) return '—';
    const [h, m] = String(t).split(':').map(Number);
    if (!Number.isFinite(h)) return t;
    const ampm = h >= 12 ? 'pm' : 'am';
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${String(m).padStart(2,'0')}${ampm}`;
  };
  const fmtDay = (d) => {
    const today = new Date().toISOString().slice(0,10);
    if (d === today) return 'TODAY';
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase();
  };

  return (
    <div style={{
      ...positionStyle,
      background: el.background,
      borderRadius: '1cqh',
      padding: '1.4cqh 1.4cqw',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
      color: el.color,
      fontFamily: el.fontFamily ? `"${el.fontFamily}", system-ui, sans-serif` : undefined,
    }}>
      <div style={{ fontSize: '1.6cqh', fontWeight: 800, textTransform: 'uppercase',
        letterSpacing: '0.15em', opacity: 0.9, marginBottom: '1cqh',
        borderBottom: `0.3cqh solid ${el.accentColor || '#f59e0b'}`,
        paddingBottom: '0.6cqh' }}>
        {el.title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6cqh', flex: 1 }}>
        {shifts.length === 0 && (
          <div style={{ opacity: 0.6, fontSize: '1.6cqh', textAlign: 'center',
            paddingTop: '2cqh' }}>No upcoming shifts</div>
        )}
        {shifts.map((s, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center',
            gap: '1cqw', fontSize: '2cqh' }}>
            <div style={{ width: '0.6cqh', height: '3cqh',
              background: s.branchColor || el.accentColor || '#f59e0b',
              borderRadius: '0.3cqh', flexShrink: 0 }} />
            <div style={{ flex: 1, fontWeight: 600, overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</div>
            <div style={{ fontSize: '1.4cqh', opacity: 0.7, fontWeight: 600,
              fontFamily: '"Space Mono", monospace, ui-monospace',
              minWidth: '4cqw' }}>{fmtDay(s.date)}</div>
            <div style={{ fontFamily: '"Space Mono", monospace, ui-monospace',
              fontSize: '1.6cqh', fontWeight: 700,
              color: el.accentColor || '#f59e0b' }}>
              {fmtTime(s.start)}–{fmtTime(s.end)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// PowerPoint Phase C — chart renderer. Renders bar/column/line/pie/donut
// charts as inline SVG using only the element's own data (no fetches).
// Drawing is done in a 200×100 viewBox so labels stay readable at any
// canvas size; the surrounding wrapper handles title, axis, and legend.
function ChartEl({ el, positionStyle }) {
  const colors = (el.colors && el.colors.length > 0)
    ? el.colors
    : ['#7c3aed','#ec4899','#f59e0b','#10b981','#06b6d4','#3b82f6'];
  const data   = Array.isArray(el.data)   ? el.data   : [];
  const labels = Array.isArray(el.labels) ? el.labels : data.map((_, i) => String(i + 1));
  const kind   = el.kind || 'bar';
  const max    = Math.max(1, ...data.map((n) => Math.abs(Number(n) || 0)));
  const total  = data.reduce((s, n) => s + Math.max(0, Number(n) || 0), 0);
  const fontFamily = el.fontFamily || 'DM Sans';
  const textColor  = el.textColor  || '#0f172a';
  const gridColor  = el.gridColor  || 'rgba(15,23,42,0.1)';
  const bg         = el.background || 'rgba(255,255,255,0.95)';

  // Container layout: title row → chart body → optional legend.
  return (
    <div style={{
      ...positionStyle,
      background: bg, borderRadius: 8, padding: '8px 10px',
      display: 'flex', flexDirection: 'column', color: textColor,
      fontFamily,
    }}>
      {el.title && (
        <div style={{ fontSize: '7%', fontWeight: 700, marginBottom: 4,
          lineHeight: 1.2, flexShrink: 0 }}>{el.title}</div>
      )}
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {(kind === 'bar' || kind === 'column') && (
          <ColumnOrBarChart kind={kind} data={data} labels={labels}
            max={max} colors={colors} showAxis={el.showAxis !== false}
            showValues={!!el.showValues} gridColor={gridColor}
            textColor={textColor} />
        )}
        {kind === 'line' && (
          <LineChart data={data} labels={labels} max={max}
            color={colors[0]} showAxis={el.showAxis !== false}
            showValues={!!el.showValues} gridColor={gridColor}
            textColor={textColor} />
        )}
        {(kind === 'pie' || kind === 'donut') && (
          <PieOrDonutChart data={data} labels={labels} total={total}
            colors={colors} kind={kind} showValues={!!el.showValues}
            textColor={textColor} />
        )}
      </div>
      {el.showLegend !== false && labels.some(Boolean) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px',
          marginTop: 6, fontSize: '5%', lineHeight: 1.2, flexShrink: 0 }}>
          {labels.slice(0, data.length).map((lab, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center',
              gap: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2,
                background: colors[i % colors.length], flexShrink: 0 }} />
              <span style={{ opacity: 0.85 }}>{lab || `#${i + 1}`}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Bar (horizontal) / column (vertical) variant of the chart.
function ColumnOrBarChart({ kind, data, labels, max, colors,
                            showAxis, showValues, gridColor, textColor }) {
  if (!data.length) return null;
  const isVertical = kind === 'column';
  const PAD = { l: 14, r: 4, t: 4, b: showAxis ? 14 : 4 };
  const VW = 200, VH = 100;
  const innerW = VW - PAD.l - PAD.r;
  const innerH = VH - PAD.t - PAD.b;
  const slot   = isVertical ? innerW / data.length : innerH / data.length;
  const gap    = slot * 0.2;
  const barT   = slot - gap;
  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} preserveAspectRatio="none"
         style={{ width: '100%', height: '100%', display: 'block',
                  fontFamily: 'inherit' }}>
      {/* gridlines (4 evenly-spaced) */}
      {showAxis && [0.25, 0.5, 0.75, 1].map((f) =>
        isVertical ? (
          <line key={f} x1={PAD.l} x2={VW - PAD.r}
            y1={PAD.t + innerH * (1 - f)} y2={PAD.t + innerH * (1 - f)}
            stroke={gridColor} strokeWidth="0.4" />
        ) : (
          <line key={f} y1={PAD.t} y2={VH - PAD.b}
            x1={PAD.l + innerW * f} x2={PAD.l + innerW * f}
            stroke={gridColor} strokeWidth="0.4" />
        )
      )}
      {/* bars */}
      {data.map((v, i) => {
        const len = (Math.max(0, Number(v) || 0) / max) * (isVertical ? innerH : innerW);
        const color = colors[i % colors.length];
        if (isVertical) {
          const x = PAD.l + slot * i + gap / 2;
          const y = PAD.t + innerH - len;
          return (
            <g key={i}>
              <rect x={x} y={y} width={barT} height={len} fill={color}
                rx="1.2" />
              {showValues && (
                <text x={x + barT / 2} y={y - 1.5}
                  fontSize="4.5" fill={textColor} textAnchor="middle">
                  {fmtVal(v)}
                </text>
              )}
            </g>
          );
        }
        const x = PAD.l;
        const y = PAD.t + slot * i + gap / 2;
        return (
          <g key={i}>
            <rect x={x} y={y} width={len} height={barT} fill={color}
              rx="1.2" />
            {showValues && (
              <text x={x + len + 1.5} y={y + barT / 2 + 1.5}
                fontSize="4.5" fill={textColor}>{fmtVal(v)}</text>
            )}
          </g>
        );
      })}
      {/* axis labels */}
      {showAxis && data.map((_, i) => {
        if (isVertical) {
          const x = PAD.l + slot * i + slot / 2;
          return (
            <text key={i} x={x} y={VH - PAD.b + 5}
              fontSize="4.5" fill={textColor} textAnchor="middle"
              opacity="0.7">{labels[i] || ''}</text>
          );
        }
        const y = PAD.t + slot * i + slot / 2 + 1.5;
        return (
          <text key={i} x={PAD.l - 1.5} y={y}
            fontSize="4.5" fill={textColor} textAnchor="end"
            opacity="0.7">{labels[i] || ''}</text>
        );
      })}
    </svg>
  );
}

// Line chart variant — single series across the X axis.
function LineChart({ data, labels, max, color, showAxis, showValues,
                    gridColor, textColor }) {
  if (data.length < 2) return null;
  const PAD = { l: 14, r: 4, t: 4, b: showAxis ? 14 : 4 };
  const VW = 200, VH = 100;
  const innerW = VW - PAD.l - PAD.r;
  const innerH = VH - PAD.t - PAD.b;
  const step = innerW / (data.length - 1);
  const pts = data.map((v, i) => {
    const x = PAD.l + step * i;
    const y = PAD.t + innerH - (Math.max(0, Number(v) || 0) / max) * innerH;
    return [x, y];
  });
  const linePath = pts.map(([x, y], i) => (i ? `L${x} ${y}` : `M${x} ${y}`)).join(' ');
  const areaPath = `${linePath} L${PAD.l + innerW} ${PAD.t + innerH} L${PAD.l} ${PAD.t + innerH} Z`;
  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} preserveAspectRatio="none"
         style={{ width: '100%', height: '100%', display: 'block',
                  fontFamily: 'inherit' }}>
      {showAxis && [0.25, 0.5, 0.75, 1].map((f) => (
        <line key={f} x1={PAD.l} x2={VW - PAD.r}
          y1={PAD.t + innerH * (1 - f)} y2={PAD.t + innerH * (1 - f)}
          stroke={gridColor} strokeWidth="0.4" />
      ))}
      <path d={areaPath} fill={color} opacity="0.15" />
      <path d={linePath} fill="none" stroke={color} strokeWidth="1.4"
        strokeLinejoin="round" strokeLinecap="round" />
      {pts.map(([x, y], i) => (
        <g key={i}>
          <circle cx={x} cy={y} r="1.6" fill={color} />
          {showValues && (
            <text x={x} y={y - 2.5} fontSize="4" fill={textColor}
              textAnchor="middle">{fmtVal(data[i])}</text>
          )}
        </g>
      ))}
      {showAxis && data.map((_, i) => {
        const x = PAD.l + step * i;
        return (
          <text key={i} x={x} y={VH - PAD.b + 5}
            fontSize="4" fill={textColor} textAnchor="middle"
            opacity="0.7">{labels[i] || ''}</text>
        );
      })}
    </svg>
  );
}

// Pie / donut variant — slices laid out clockwise from 12 o'clock.
function PieOrDonutChart({ data, labels, total, colors, kind,
                          showValues, textColor }) {
  if (total <= 0) return null;
  const VW = 100, VH = 100;
  const cx = VW / 2, cy = VH / 2;
  const rOuter = 45;
  const rInner = kind === 'donut' ? rOuter * 0.55 : 0;
  let angle = -Math.PI / 2;
  const slices = [];
  data.forEach((v, i) => {
    const pct = Math.max(0, Number(v) || 0) / total;
    if (pct <= 0) return;
    const a0 = angle;
    const a1 = angle + pct * Math.PI * 2;
    angle = a1;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const x0 = cx + rOuter * Math.cos(a0);
    const y0 = cy + rOuter * Math.sin(a0);
    const x1 = cx + rOuter * Math.cos(a1);
    const y1 = cy + rOuter * Math.sin(a1);
    let path;
    if (rInner > 0) {
      const ix0 = cx + rInner * Math.cos(a0);
      const iy0 = cy + rInner * Math.sin(a0);
      const ix1 = cx + rInner * Math.cos(a1);
      const iy1 = cy + rInner * Math.sin(a1);
      path = `M${x0} ${y0} A${rOuter} ${rOuter} 0 ${large} 1 ${x1} ${y1}`
           + ` L${ix1} ${iy1} A${rInner} ${rInner} 0 ${large} 0 ${ix0} ${iy0} Z`;
    } else {
      path = `M${cx} ${cy} L${x0} ${y0} A${rOuter} ${rOuter} 0 ${large} 1 ${x1} ${y1} Z`;
    }
    const mid = (a0 + a1) / 2;
    const labelR = (rOuter + rInner) / 2;
    slices.push({
      path,
      color: colors[i % colors.length],
      pct,
      label: labels[i] || `#${i + 1}`,
      lx: cx + labelR * Math.cos(mid),
      ly: cy + labelR * Math.sin(mid),
    });
  });
  return (
    <svg viewBox={`0 0 ${VW} ${VH}`}
         style={{ width: '100%', height: '100%', display: 'block',
                  fontFamily: 'inherit' }}>
      {slices.map((s, i) => (
        <g key={i}>
          <path d={s.path} fill={s.color} stroke="#fff" strokeWidth="0.5" />
          {showValues && s.pct >= 0.04 && (
            <text x={s.lx} y={s.ly + 1.5} fontSize="4"
              fill="#fff" fontWeight="700" textAnchor="middle">
              {Math.round(s.pct * 100)}%
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}

// Compact number formatter for chart value labels (12,345 → 12.3k).
function fmtVal(v) {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (Math.abs(n) >= 1e3) return (n / 1e3).toFixed(1) + 'k';
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(1);
}

// Minimal QR encoder using the canonical QR algorithm. Implementation below
// is kept self-contained (no external deps) so the player works offline.
// Modules render as SVG <rect> tiles; surrounding "quiet zone" is the
// container's padding.
function QrSvg({ data, fg, bg }) {
  const matrix = encodeQR(data);
  const size = matrix.length;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} xmlns="http://www.w3.org/2000/svg"
         style={{ width: '100%', height: '100%', display: 'block' }}>
      <rect width={size} height={size} fill={bg} />
      {matrix.flatMap((row, y) =>
        row.map((on, x) => on
          ? <rect key={`${x}-${y}`} x={x} y={y} width="1.02" height="1.02" fill={fg} />
          : null
        )
      )}
    </svg>
  );
}

// ─── Tiny QR encoder ──────────────────────────────────────────────────────
// Source-of-truth implementation lifted from the public-domain qrcode-svg
// reference. Supports byte-mode encoding, EC level L, automatic version
// selection up to v10 (capacity ~213 alphanumeric / 174 byte chars). For
// signage URLs and short text that's plenty.
function encodeQR(text) {
  const bytes = new TextEncoder().encode(text);
  for (let v = 1; v <= 10; v++) {
    try { return _qrEncode(bytes, v); } catch { /* try next version */ }
  }
  throw new Error('QR data too long (>v10)');
}
function _qrEncode(bytes, version) {
  const size = 17 + 4 * version;
  const totalCodewords = [, 26, 44, 70, 100, 134, 172, 196, 242, 292, 346][version];
  const ecCodewords    = [,  7, 10, 15, 20, 26, 18, 20, 24, 30, 18][version];
  const dataCodewords = totalCodewords - ecCodewords;
  if (bytes.length + 2 > dataCodewords) throw new Error('too long for version ' + version);

  // Bitstream: mode(4) + length(8) + bytes + terminator + pad
  const bits = [];
  const pushBits = (v, n) => { for (let i = n - 1; i >= 0; i--) bits.push((v >> i) & 1); };
  pushBits(0b0100, 4);
  pushBits(bytes.length, 8);
  for (const b of bytes) pushBits(b, 8);
  for (let i = 0; i < 4 && bits.length < dataCodewords * 8; i++) bits.push(0);
  while (bits.length % 8) bits.push(0);
  const codewords = [];
  for (let i = 0; i < bits.length; i += 8) {
    let b = 0;
    for (let j = 0; j < 8; j++) b = (b << 1) | bits[i + j];
    codewords.push(b);
  }
  const padBytes = [0xEC, 0x11];
  while (codewords.length < dataCodewords) codewords.push(padBytes[(codewords.length - dataCodewords) & 1 ? 0 : 1]);

  // Reed-Solomon ECC
  const gf = _gfTables();
  const generator = _rsGenerator(ecCodewords, gf);
  const ecc = _rsEncode(codewords, generator, gf);
  const all = codewords.concat(ecc);

  // Place modules: function patterns + data, then mask
  const m = Array.from({ length: size }, () => new Array(size).fill(null));
  _placeFinders(m, size);
  _placeTiming(m, size);
  m[size - 8][8] = 1;
  if (version >= 7) _placeVersion(m, size, version);
  _placeFormat(m, size, 0, 0);    // tentative; fixed after mask
  _placeData(m, all, size);
  const { masked, mask } = _bestMask(m, size);
  _placeFormat(masked, size, 0 /* L */, mask);
  return masked;
}
function _gfTables() {
  const exp = new Uint8Array(512), log = new Uint8Array(256);
  let x = 1;
  for (let i = 0; i < 255; i++) { exp[i] = x; log[x] = i; x = (x << 1) ^ (x & 0x80 ? 0x11D : 0); }
  for (let i = 255; i < 512; i++) exp[i] = exp[i - 255];
  const mul = (a, b) => (a && b) ? exp[log[a] + log[b]] : 0;
  return { exp, log, mul };
}
function _rsGenerator(degree, gf) {
  let g = [1];
  for (let i = 0; i < degree; i++) {
    const ng = new Array(g.length + 1).fill(0);
    for (let j = 0; j < g.length; j++) {
      ng[j]     ^= g[j];
      ng[j + 1] ^= gf.mul(g[j], gf.exp[i]);
    }
    g = ng;
  }
  return g;
}
function _rsEncode(data, gen, gf) {
  const ec = new Array(gen.length - 1).fill(0);
  for (const b of data) {
    const factor = b ^ ec[0];
    ec.shift(); ec.push(0);
    if (factor) for (let i = 0; i < gen.length - 1; i++) ec[i] ^= gf.mul(gen[i + 1], factor);
  }
  return ec;
}
function _placeFinders(m, size) {
  const draw = (cx, cy) => {
    for (let dy = -1; dy <= 7; dy++) for (let dx = -1; dx <= 7; dx++) {
      const x = cx + dx, y = cy + dy;
      if (x < 0 || y < 0 || x >= size || y >= size) continue;
      const inOuter = (dx >= 0 && dx <= 6 && (dy === 0 || dy === 6)) ||
                      (dy >= 0 && dy <= 6 && (dx === 0 || dx === 6));
      const inInner = dx >= 2 && dx <= 4 && dy >= 2 && dy <= 4;
      m[y][x] = inOuter || inInner ? 1 : 0;
    }
  };
  draw(0, 0); draw(size - 7, 0); draw(0, size - 7);
}
function _placeTiming(m, size) {
  for (let i = 8; i < size - 8; i++) {
    if (m[6][i] === null) m[6][i] = i % 2 === 0 ? 1 : 0;
    if (m[i][6] === null) m[i][6] = i % 2 === 0 ? 1 : 0;
  }
}
function _placeFormat(m, size, ec, mask) {
  // 5 bits = (ec << 3) | mask, then BCH(15,5) + XOR with 0x5412
  const data = (ec << 3) | mask;
  let rem = data;
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >> 9) * 0x537);
  const bits = ((data << 10) | (rem & 0x3FF)) ^ 0x5412;
  for (let i = 0; i <= 5; i++)        m[i][8] = (bits >> i) & 1;
  m[7][8] = (bits >> 6) & 1;
  m[8][8] = (bits >> 7) & 1;
  m[8][7] = (bits >> 8) & 1;
  for (let i = 9; i < 15; i++)        m[8][14 - i] = (bits >> i) & 1;
  for (let i = 0; i <= 7; i++)        m[size - 1 - i][8] = (bits >> i) & 1;
  for (let i = 8; i < 15; i++)        m[8][size - 15 + i] = (bits >> i) & 1;
}
function _placeVersion(m, size, ver) {
  let rem = ver;
  for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >> 11) * 0x1F25);
  const bits = (ver << 12) | (rem & 0xFFF);
  for (let i = 0; i < 18; i++) {
    const a = Math.floor(i / 3), b = (i % 3) + size - 11, on = (bits >> i) & 1;
    m[a][b] = on; m[b][a] = on;
  }
}
function _placeData(m, codewords, size) {
  let bitIdx = 0, dir = -1;
  for (let col = size - 1; col > 0; col -= 2) {
    if (col === 6) col -= 1;
    for (let i = 0; i < size; i++) {
      const y = dir < 0 ? size - 1 - i : i;
      for (let dx = 0; dx < 2; dx++) {
        const x = col - dx;
        if (m[y][x] !== null) continue;
        const byte = codewords[bitIdx >> 3];
        const bit = byte === undefined ? 0 : (byte >> (7 - (bitIdx & 7))) & 1;
        m[y][x] = bit;
        bitIdx++;
      }
    }
    dir = -dir;
  }
}
function _bestMask(m, size) {
  let best = null;
  for (let mask = 0; mask < 8; mask++) {
    const cand = m.map((row, y) => row.map((v, x) => {
      const isFn = _isFunction(x, y, size);
      if (isFn || v === null) return v === null ? 0 : v;
      return v ^ _maskBit(mask, x, y);
    }));
    const score = _penalty(cand, size);
    if (!best || score < best.score) best = { masked: cand, mask, score };
  }
  return best;
}
function _isFunction(x, y, size) {
  if ((x < 9 && y < 9) || (x >= size - 8 && y < 9) || (x < 9 && y >= size - 8)) return true;
  if (x === 6 || y === 6) return true;
  return false;
}
function _maskBit(mask, x, y) {
  switch (mask) {
    case 0: return ((x + y) % 2) === 0 ? 1 : 0;
    case 1: return (y % 2) === 0 ? 1 : 0;
    case 2: return (x % 3) === 0 ? 1 : 0;
    case 3: return ((x + y) % 3) === 0 ? 1 : 0;
    case 4: return ((Math.floor(y/2) + Math.floor(x/3)) % 2) === 0 ? 1 : 0;
    case 5: return ((x*y) % 2 + (x*y) % 3) === 0 ? 1 : 0;
    case 6: return (((x*y) % 2 + (x*y) % 3) % 2) === 0 ? 1 : 0;
    case 7: return (((x+y) % 2 + (x*y) % 3) % 2) === 0 ? 1 : 0;
  }
  return 0;
}
function _penalty(m, size) {
  let p = 0;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) if (m[y][x]) p++;
  }
  return Math.abs(p * 100 / (size*size) - 50);   // simplified; good-enough
}

// ─── fallback ─────────────────────────────────────────────────────────────
function FallbackRenderer({ message }) {
  return (
    <div style={{
      ...baseRoot('#08061a', '#9ca3af'),
      alignItems: 'center', justifyContent: 'center',
      textAlign: 'center', padding: '4cqh',
      fontFamily: "'Space Mono', monospace", fontSize: 14,
    }}>{message}</div>
  );
}
